import { HttpRequestOptions, HttpResponse } from '../api'
import axios, { Method } from 'axios'
import { HttpMethod } from '../api/impl/runtime'
import { AuthHttpClient, AuthHttpClientMiddleware } from '../features/authorization/data/authHttpClient'
import { runIfDefined } from './tools'
import { Logger } from './logger'
import { Context } from './context'
import { AppErrorCode, throwError, throwInternalError } from './errors'
import { HttpHeader, parseJsonAppError } from './httpTools'

const AXIOS_METHODS_MAPPING: Record<HttpMethod, Method> = {
  GET: 'get',
  POST: 'post',
  PUT: 'put',
  PATCH: 'patch',
  DELETE: 'delete',
}

export class AxiosAuthHttpClient implements AuthHttpClient {
  authBearerToken: (() => Promise<string>) | undefined

  private logger = new Logger('AxiosAuthHttpClient')
  private middlewares: AuthHttpClientMiddleware[] = []

  constructor(private opts: { baseUrl: string | (() => Promise<string>) }) {}

  addMiddleware(middleware: AuthHttpClientMiddleware): void {
    this.middlewares.push(middleware)
  }

  async exec(ctx: Context, url: string, options: HttpRequestOptions): Promise<HttpResponse> {
    this.logger.debug(ctx, '<68f76acf> Request', { ...options, url })

    const token = await runIfDefined(this.authBearerToken, t => t())

    let baseURL: string
    if (typeof this.opts.baseUrl === 'string') {
      baseURL = this.opts.baseUrl
    } else {
      baseURL = await this.opts.baseUrl()
    }

    const response = await axios({
      method: AXIOS_METHODS_MAPPING[options.method],
      baseURL,
      headers: {
        Authorization: runIfDefined(token, t => `Bearer ${t}`),
        ...options.headers,
      },
      url,
      data: options.body,
      validateStatus: () => true,
    })
    this.logger.debug(ctx, '<fbffcb31> Response', {
      status: response.status,
      data: response.data,
      headers: response.headers,
    })

    const r: HttpResponse = {
      status: response.status,
      headers: {
        get: name => {
          const n = name.toLowerCase()
          for (const k of Object.keys(response.headers)) {
            if (k.toLowerCase() === n) {
              const v = response.headers[k]
              if (v !== undefined && typeof v !== 'string') throwInternalError('<9ae5ee44>')
              return v
            }
          }
          return undefined
        },
      },
      json: async () => {
        return response.data
      },
    }

    for (const m of this.middlewares) {
      await m.handleResponse(r)
    }

    if (response.status !== 200) {
      const data = await r.json()
      if (typeof data === 'object' && data !== null) {
        const error = parseJsonAppError(r.headers.get(HttpHeader.CONTENT_TYPE), data)
        if (error) throwError(error.code as AppErrorCode, error.message, { uid: error.uid })
      }
      throwError(AppErrorCode.NETWORK_ERROR, `<556d394c> Invalid response status: ${response.status}`)
    }

    return r
  }
}
